import videojs from 'video.js';

const _chaptersTrack = new WeakMap();
const _enabled = new WeakMap();
const _showChapterList = new WeakMap();

const DEFAULT_CHAPTER_SPACER_WIDTH_PIXELS = 6;
let localize;

/**
 * The Chapters module.
 * @member {ChaptersPlugin} chapters
 * @memberof MediasitePlayer#
 */

/**
 * Configures a list of chapters that will be available to the user in the UI.
 * @hideconstructor
 * @extends {external:"videojs.Plugin"}
 **/
class ChaptersPlugin extends videojs.getPlugin('plugin') {
    constructor(player, options) {
        localize = player.localize?.bind(player);
        super(player, options);

        const track = new videojs.TextTrack({
            tech: player,
            kind: 'chapters',
            label: localize('Chapters'),
            language: 'en',
            default: true
        });
        this.on(track, 'cuechange', this.#handleChapterChange);
        _chaptersTrack.set(this, track);

        const tracks = this.player.textTracks();
        tracks.addTrack(track);

        player.ready(() => {
            player.collapsedButtons?.push({
                pluginName: this.name,
                label: localize('Chapters'),
                buttonName: 'ChaptersButton',
                class: 'vjs-chapters-button'
            });
        });
    }

    /**
     * Adds the given chapter to the player and returns its ID.
     * @param {VTTCue} chapter
     * @returns {string}
     * @example
     * player.chapters.add(new VTTCue(10, 20, 'A chapter that starts at 10 seconds and ends at 20 seconds.'));
     */
    add(chapter) {
        const track = _chaptersTrack.get(this);
        track.addCue(chapter);

        const ChaptersTrackMenuItem = videojs.getComponent('ChaptersTrackMenuItem');
        const menuItem = new ChaptersTrackMenuItem(this.player, { track, cue: chapter });
        const chaptersButton = this.player.getDescendant('ControlBar', 'ChaptersButton');
        chaptersButton.menu.addItem(menuItem);

        this.player.seekBarSegments().add({
            id: chapter.id,
            startTime: chapter.startTime,
            endTime: chapter.endTime,
            label: chapter.text,
            spacerWidth: DEFAULT_CHAPTER_SPACER_WIDTH_PIXELS,
            progress: true
        });

        this.#updateVisibility();

        return chapter.id;
    }

    /**
     * Removes the chapter with the given ID from the player.
     * @param {string} id
     * @example
     * player.chapters.remove('chapterId');
     */
    remove(id) {
        const track = _chaptersTrack.get(this);
        const chaptersButton = this.player.getDescendant('ControlBar', 'ChaptersButton');

        for (const cue of Array.from(track.cues)) {
            if (cue.id === id) {
                const menuItem = chaptersButton.menu.children().find(item => item.cue?.id === id);
                chaptersButton.menu.removeChild(menuItem);
                track.removeCue(cue);
                break;
            }
        }

        this.player.seekBarSegments().remove(id);

        this.#updateVisibility();
    }

    /**
     * Gets the chapter with the given ID.
     * @param {string} id
     * @returns {VTTCue}
     * @example
     * const chapterCue = player.chapters.get('chapterId');
     */
    get(id) {
        const track = _chaptersTrack.get(this);
        return Array.from(track.cues).find(cue => cue.id === id) ?? null;
    }

    /**
     * Gets an array containing all available chapters.
     * @returns {VTTCue[]}
     * @example
     * const allChapters = player.chapters.getAll();
     **/
    getAll() {
        const track = _chaptersTrack.get(this);
        return Array.from(track.cues);
    }

    /**
     * Called by the hotkey, this presses the chapters button so that the list of chapters is displayed and the focus is within it
     */
    toggleChaptersButton() {
        const chaptersButton = this.player.getDescendant('ControlBar', 'ChaptersButton');
        chaptersButton.pressButton();
    }

    #handleChapterChange() {
        const track = _chaptersTrack.get(this);
        const currentChapter = track.activeCues[0] ?? null;
        this.player.trigger('chapterchanged', currentChapter);
    }

    #updateVisibility() {
        const enabled = _enabled.get(this);
        const track = _chaptersTrack.get(this);
        const cues = Array.from(track.cues);

        // Logic for hidiing or showing chapters button and timeline markers
        if (enabled && cues.length > 0) {
            //if seeking is disabled, hide the option to select a chapter, but still display the chapter segments
            const showChapterList = _showChapterList.get(this);
            
            this.#setVisiblityofChapterButton(showChapterList);
            for (const { id } of cues) {
                this.player.seekBarSegments().update(id, {});
            }
            this.#updateTimeTooltip();
        }
        else {
            // Hide chapters button and timeline markers
            this.#setVisiblityofChapterButton(false);
            for (const { id } of cues) {
                this.player.seekBarSegments().update(id, null);
            }
        }
    }

    #setVisiblityofChapterButton(visiblity) {
        const chaptersButton = this.player.getDescendant('ControlBar', 'ChaptersButton');
        if (visiblity) {
            chaptersButton.removeClass('vjs-hidden');
        } else {
            chaptersButton.addClass('vjs-hidden');
        }
    }

    #updateTimeTooltip() {
        const player = this.player
        const timeTooltip = player.getChild('ControlBar')?.getChild('ProgressControl')?.getChild('SeekBar')?.getChild('MouseTimeDisplay')?.getChild('TimeTooltip');
        const track = _chaptersTrack.get(this);
        const cues = Array.from(track.cues)

        if (timeTooltip != null) {
            // Videojs has internal logic to determine when the tooltip should be updated, and how to handle the edges of screen
            // want to add logic to add chapter names, without losing any logic from future videojs updates by overwriting
            // So overwrite update function with function containing update function, plus added chapterName logic
            timeTooltip.update = (function () {
                const cached_timeTooltipUpdate = timeTooltip.update;
                return function (seekBarRect, seekBarPoint, time) {
                    cached_timeTooltipUpdate.apply(this, arguments);
                    const timeNumber = seekBarPoint * player.duration();
                    let chapterName = '';

                    for (const cue of cues) {
                        if (timeNumber >= cue.startTime && timeNumber < cue.endTime) {
                            chapterName = cue.text;
                            break;
                        }
                        else if (timeNumber === cue.endTime && timeNumber === player.duration()) {
                            chapterName = cue.text;
                            break;
                        }
                    }

                    if (chapterName === '') {
                        this.write(`${time}`)
                    }
                    else {
                        this.write(`${time} - ${chapterName}`)
                    }
                }
            }());
        }
    }

    /**
     * Whether or not to enable the chapters UI.
     * @type {boolean}
     * @example
     * player.chapters.enabled = true;
     */
    get enabled() {
        return _enabled.get(this) ?? false;
    }

    set enabled(enabled) {
        _enabled.set(this, enabled);
        this.#updateVisibility();
    }

    /**
     * Whether or not to display just the chapters button.
     * @type {boolean}
     * @example
     * player.chapters.showChapterList = true;
     */
    get showChapterList() {
        return _showChapterList.get(this) ?? false;
    }

    set showChapterList(showChapterList) {
        _showChapterList.set(this, showChapterList);
        this.#updateVisibility();
    }
}

export default ChaptersPlugin;
